import { Inject, Injectable } from '@angular/core';
import { differenceWith, find, includes, isEqual, reduce } from 'lodash';
import { IDocLog } from 'src/app/shared/models/doc-log/doc-log';
import { IPointPerson } from '../../typings/interfaces/student.interface';
import { IUserMini } from '../../typings/interfaces/user.interface';
import { DateHelpers } from './../../services/date-helpers/date-helpers.service';
import { PathParserService } from './../../services/side-nav/path-parser.service';

export interface IHistoryRecord {
  id: string;
  condensed: string;
  expanded: string;
  age: string;
  panelOpen: boolean;
  createdAt: string;
}

export interface IHistoryRecordText {
  condensed: string;
  expanded: string;
}

const flagPPTypes = ['ATTENDANCE', 'ACADEMIC', 'POSTSECONDARY', 'REGENTS', 'CREDITS'];

@Injectable()
export class HistoryAllHelperService {
  constructor(
    private dateHelpers: DateHelpers,
    private pathParserService: PathParserService,
    @Inject('Rollbar') private Rollbar,
  ) {
    //
  }

  // creates history card data based on docLog path (JE)
  constructHistoryRecord(docLog: IDocLog): IHistoryRecord {
    const { createdAt, historyType } = docLog;
    const time = this.dateHelpers.getMomentSinceVariableUnit(createdAt);
    const age = `${time.count}${time.unit}`;
    const panelOpen = false;
    let historyRecord = null;
    // create condensed and expanded text based on recordType (JE)
    switch (historyType) {
      case 'STUDENT_EDIT':
        historyRecord = this.createEditHistoryRecord(docLog);
        break;
      case 'STUDENT_SUPPORT_ASSIGN':
        historyRecord = this.createAssignHistoryRecord(docLog);
        break;
      case 'STUDENT_SUPPORT_UNASSIGN':
        historyRecord = this.createUnassignHistoryRecord(docLog);
        break;
      case 'STUDENT_SUPPORT_COMPLETE':
        historyRecord = this.createCompleteHistoryRecord(docLog);
        break;
      case 'NOTE_ADDED':
        historyRecord = this.createNoteAddedHistoryRecord(docLog);
        break;
      case 'NOTE_UPDATED':
        historyRecord = this.createNoteUpdatedHistoryRecord(docLog);
        break;
      case 'FLAG_RESOLVED':
        historyRecord = this.createFlagResolvedHistoryRecord(docLog);
        break;
      case 'FLAG_ACTIVATED':
        historyRecord = this.createFlagActivatedHistoryRecord(docLog);
        break;
      case 'FLAG_IGNORED':
        historyRecord = this.createFlagIgnoredHistoryRecord(docLog);
        break;
      case 'STUDENT_POINT_PERSON':
        historyRecord = this.createPointPeopleEditRecord(docLog);
        break;
      case 'MILESTONE_COMPLETE':
        historyRecord = this.createMilestoneRecord(docLog, 'complete');
        break;
      case 'MILESTONE_INCOMPLETE':
        historyRecord = this.createMilestoneRecord(docLog, 'incomplete');
        break;
      case 'STUDENT_PATH_ADDED':
        historyRecord = this.createStudentPathAddedRecord(docLog);
        break;
      case 'STUDENT_PATH_REMOVED':
        historyRecord = this.createStudentPathRemovedRecord(docLog);
        break;
      case 'STUDENT_PATH_UPDATED':
        historyRecord = this.createStudentPathStatusRecord(docLog);
        break;
      case 'STUDENT_ACTIVITY_ADDED':
        historyRecord = this.createStudentActivityAssignedRecord(docLog);
        break;
      case 'STUDENT_ACTIVITY_REMOVED':
        historyRecord = this.createStudentActivityRemovedRecord(docLog);
        break;
      case 'STUDENT_ACTIVITY_UPDATED':
        historyRecord = this.createStudentActivityUpdatedRecord(docLog);
        break;
      case 'STUDENT_ASSESSMENT_SCORE_UPDATE':
        historyRecord = this.createStudentAssessmentScoreRecord(docLog);
        break;
      default:
        break;
    }

    let expanded;
    let condensed;
    if (historyRecord) {
      condensed = historyRecord.condensed;
      expanded = historyRecord.expanded;
      return {
        id: docLog._id,
        condensed,
        expanded,
        age,
        panelOpen,
        createdAt,
      };
    } else {
      return null;
    }
  }

  parseCreatedBy(user: IUserMini): string {
    const { firstName, lastName } = user;
    return `${firstName} ${lastName.substring(0, 1)}.`;
  }

  truncate(value: string): string {
    if (!value.length) return '';
    return value.length > 20 ? value.substring(0, 19) + '...' : value;
  }

  createEditHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      diffs,
      metaData: { fieldHuman },
    } = docLog;
    const editableField = fieldHuman;
    const editableFieldCropped = this.truncate(editableField);

    let oldVal = diffs[0].oldVal || '\u2014';
    // catch case where newVal is an array (JE)
    if (oldVal.length === 0) oldVal = '\u2014';
    let newVal = diffs[0].newVal || '\u2014';
    if (newVal.length === 0) newVal = '\u2014';

    const newValueCropped = this.truncate(newVal);
    const user = this.parseCreatedBy(createdBy);
    const condensed =
      `Changed <span class="history-value">${editableFieldCropped}</span> ` +
      `to <span class="history-value">${newValueCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> changed ` +
      `<span class="history-value">${editableField}</span> ` +
      `from <span class="history-value">${oldVal}</span> ` +
      `to <span class="history-value">${newVal}</span>`;
    return { condensed, expanded };
  }

  createAssignHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const { newDoc, createdBy } = docLog;
    const supportName = newDoc.support.name;
    const supportCropped = this.truncate(supportName);
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Assigned to <span class="history-value">${supportCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> assigned student to ` +
      `<span class="history-value">${supportName}</span>`;
    return { condensed, expanded };
  }

  createUnassignHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      metaData: { supportName },
    } = docLog;
    const supportCropped = this.truncate(supportName);
    const user = this.parseCreatedBy(createdBy);
    const condensed = `Unassigned from <span class="history-value">${supportCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> unassigned student from ` +
      `<span class="history-value">${supportName}</span>`;
    return { condensed, expanded };
  }

  createCompleteHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      metaData: { supportName },
    } = docLog;
    const supportCropped = this.truncate(supportName);
    const user = this.parseCreatedBy(createdBy);
    const condensed = `Marked complete in <span class="history-value">${supportCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> marked student ` +
      `complete in <span class="history-value">${supportName}</span>`;
    return { condensed, expanded };
  }

  createNoteAddedHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      newDoc: { body },
      createdBy,
    } = docLog;
    const noteCropped = this.truncate(body);
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Note: <span class="history-value">${noteCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> noted: ` + `<span class="history-value">${body}</span>`;
    return { condensed, expanded };
  }

  createNoteUpdatedHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const { diffs, createdBy } = docLog;
    const note = diffs[0].newVal;
    const noteCropped = this.truncate(note);
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Note updated: <span class="history-value">${noteCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> updated: ` + `<span class="history-value">${note}</span>`;
    return { condensed, expanded };
  }

  createFlagResolvedHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      diffs,
      createdBy,
      metaData: { flagType },
    } = docLog;
    const noteDiff = find(diffs, { path: 'note' });
    const note = noteDiff ? noteDiff.newVal : '';
    const noteCropped = this.truncate(note);
    const parsedNote = noteDiff ? `With note: <span class="history-value">${note}</span>` : '';
    const parsedNoteCropped = noteDiff ? `with note: <span class="history-value">${noteCropped}</span>` : '';
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Resolved <span class="history-value">${flagType}</span> ` + `${parsedNoteCropped}`;
    const expanded =
      `<span class="history-value">${user}</span> resolved ` +
      `<span class="history-value">${flagType}</span> ` +
      `${parsedNote}`;
    return { condensed, expanded };
  }

  createFlagActivatedHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      diffs,
      createdBy,
      metaData: { flagType },
    } = docLog;
    const noteDiff = find(diffs, { path: 'note' });
    const note = noteDiff ? noteDiff.newVal : '';
    const noteCropped = this.truncate(note);
    const parsedNote = noteDiff ? `With note: <span class="history-value">${note}</span>` : '';
    const parsedNoteCropped = noteDiff ? `with note: <span class="history-value">${noteCropped}</span>` : '';
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Activated <span class="history-value">${flagType}</span> ` + `${parsedNoteCropped}`;
    const expanded =
      `<span class="history-value">${user}</span> activated ` +
      `<span class="history-value">${flagType}</span> ` +
      `${parsedNote}`;
    return { condensed, expanded };
  }

  createFlagIgnoredHistoryRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      diffs,
      createdBy,
      metaData: { flagType },
    } = docLog;
    const noteDiff = find(diffs, { path: 'note' });
    const note = noteDiff ? noteDiff.newVal : '';
    const noteCropped = this.truncate(note);
    const parsedNote = noteDiff ? `With note: <span class="history-value">${note}</span>` : '';
    const parsedNoteCropped = noteDiff ? `with note: <span class="history-value">${noteCropped}</span>` : '';
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Ignored <span class="history-value">${flagType}</span> ` + `${parsedNoteCropped}`;
    const expanded =
      `<span class="history-value">${user}</span> ignored ` +
      `<span class="history-value">${flagType}</span> ` +
      `${parsedNote}`;
    return { condensed, expanded };
  }

  createPointPeopleEditRecord(docLog: IDocLog): IHistoryRecordText {
    const { diffs, createdBy } = docLog;
    const user = this.parseCreatedBy(createdBy);
    const oldPP: IPointPerson[] = diffs[0].oldVal;
    const newPP: IPointPerson[] = diffs[0].newVal;
    const ppAdded = oldPP.length < newPP.length;
    const ppRemoved = newPP.length < oldPP.length;
    const ppEdited = oldPP.length === newPP.length;

    if (ppAdded) {
      // should be a single point person object (JE)
      const ppDiff: IPointPerson = differenceWith(newPP, oldPP, isEqual)[0];
      const isSupportLeadType = ppDiff.type === 'SUPPORT_LEAD';
      if (isSupportLeadType) return null;
      // convert `GUIDANCE_COUNSELOR` to `Guidance counselor` and `ACADEMIC` to `Academic`
      let parsedType = ppDiff.type
        .toLowerCase()
        .split('_')
        .join(' ');
      parsedType = parsedType.charAt(0).toUpperCase() + parsedType.slice(1);
      const diffString = includes(flagPPTypes, ppDiff.type) ? `${parsedType} point person` : parsedType;
      const newPPName = `${ppDiff.user.firstName} ${ppDiff.user.lastName}`;
      const condensed = `Changed <span class="history-value">${diffString}</span>`;
      const expanded =
        `<span class="history-value">${user}</span> changed` +
        ` <span class="history-value">${diffString}</span>` +
        ` from <span class="history-value">—</span> to <span class="history-value">${newPPName}</span>`;
      return { condensed, expanded };
    }

    if (ppRemoved) {
      const ppDiff: IPointPerson = differenceWith(oldPP, newPP, isEqual)[0];
      const isSupportLeadType = ppDiff.type === 'SUPPORT_LEAD';
      if (isSupportLeadType) return null;
      let parsedType = ppDiff.type
        .toLowerCase()
        .split('_')
        .join(' ');
      parsedType = parsedType.charAt(0).toUpperCase() + parsedType.slice(1);
      const diffString = includes(flagPPTypes, ppDiff.type) ? `${parsedType} point person` : parsedType;
      const removedPPName = `${ppDiff.user.firstName} ${ppDiff.user.lastName}`;
      const condensed = `Changed <span class="history-value">${diffString}</span>`;
      const expanded =
        `<span class="history-value">${user}</span> changed` +
        ` <span class="history-value">${diffString}</span>` +
        ` from <span class="history-value">${removedPPName}</span>` +
        ` to <span class="history-value">—</span>`;
      return { condensed, expanded };
    }

    if (ppEdited) {
      const oldPerson: IPointPerson = differenceWith(oldPP, newPP, isEqual)[0];
      const oldPersonName = `${oldPerson.user.firstName} ${oldPerson.user.lastName}`;
      const newPerson: IPointPerson = differenceWith(newPP, oldPP, isEqual)[0];
      const newPersonName = `${newPerson.user.firstName} ${newPerson.user.lastName}`;

      let parsedType = oldPerson.type
        .toLowerCase()
        .split('_')
        .join(' ');
      parsedType = parsedType.charAt(0).toUpperCase() + parsedType.slice(1);
      const diffString = includes(flagPPTypes, oldPerson.type) ? `${parsedType} point person` : parsedType;
      const condensed = `Changed <span class="history-value">${diffString}</span>`;
      const expanded =
        `<span class="history-value">${user}</span> changed` +
        ` <span class="history-value">${diffString}</span>` +
        ` from <span class="history-value">${oldPersonName}</span>` +
        ` to <span class="history-value">${newPersonName}</span>`;
      return { condensed, expanded };
    }
  }

  createMilestoneRecord(docLog: IDocLog, status: string): IHistoryRecordText {
    const {
      createdBy,
      metaData: { milestoneHuman },
    } = docLog;

    const milestoneHumanCropped = this.truncate(milestoneHuman);
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Marked <span class="history-value">${milestoneHumanCropped}</span> ${status}`;
    const expanded =
      `<span class="history-value">${user}</span> marked ` +
      `<span class="history-value">${milestoneHuman}</span> milestone <span class="history-value">${status}</span>`;
    return { condensed, expanded };
  }

  createStudentPathAddedRecord(docLog: IDocLog): IHistoryRecordText {
    //
    const {
      createdBy,
      metaData: { pathName, pathType },
    } = docLog;
    const pathTypeLower = pathType.toLowerCase();
    const studentPathCropped = this.truncate(pathName);
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Added <span class="history-value">${studentPathCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> added ` +
      `<span class="history-value">${pathName}</span> to ${pathTypeLower} list`;
    return { condensed, expanded };
  }

  createStudentPathRemovedRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      metaData: { pathName, pathType },
    } = docLog;
    const pathTypeLower = pathType.toLowerCase();
    const studentPathCropped = this.truncate(pathName);
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Removed <span class="history-value">${studentPathCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> removed ` +
      `<span class="history-value">${pathName}</span> from ${pathTypeLower} list`;
    return { condensed, expanded };
  }

  createStudentPathStatusRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      diffs,
      metaData: { pathName },
    } = docLog;
    const { oldVal, newVal } = diffs[0];
    const studentPathCropped = this.truncate(pathName);
    const statusCropped = this.truncate(newVal);
    const user = this.parseCreatedBy(createdBy);

    const condensed =
      `Changed <span class="history-value">${studentPathCropped}</span> status ` +
      `to <span class="history-value">${statusCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> changed ` +
      `<span class="history-value">${pathName}</span> from <span class="history-value">` +
      `${oldVal}</span> to <span class="history-value">${newVal}</span>`;
    return { condensed, expanded };
  }

  createStudentActivityAssignedRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      metaData: { activityName },
      newDoc: { status, completedHours },
    } = docLog;

    const activityNameCropped = this.truncate(activityName);
    const user = this.parseCreatedBy(createdBy);
    let parsedStatus;
    if (status) {
      switch (status) {
        case 'COMPLETED':
          parsedStatus = 'complete';
          break;
        case 'INCOMPLETE':
          parsedStatus = 'incomplete';
          break;
        default:
          break;
      }
    }
    const condensed = `Assigned student to <span class="history-value">${activityNameCropped}</span>`;
    let expanded =
      `<span class="history-value">${user}</span> assigned student ` +
      `to <span class="history-value">${activityName}</span>`;

    // account for assignment with hours (JE)
    if (completedHours || completedHours === 0)
      expanded += ` and changed completed hours to <span class="history-value">${completedHours}</span>`;
    if (parsedStatus) expanded += ` and marked <span class="history-value">${parsedStatus}</span>`;

    return { condensed, expanded };
  }

  createStudentActivityUpdatedRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      metaData: { activityName },
      diffs,
    } = docLog;
    const activityNameCropped = this.truncate(activityName);
    const user = this.parseCreatedBy(createdBy);
    const hoursDiff = find(diffs, diff => diff.path === 'completedHours');
    const statusDiff = find(diffs, diff => diff.path === 'status');
    let parsedStatus;
    if (statusDiff) {
      if (statusDiff.newVal === 'COMPLETED') parsedStatus = 'complete';
      if (statusDiff.newVal === 'INCOMPLETE') parsedStatus = 'incomplete';
    }
    let condensed;
    let expanded;

    if (hoursDiff) {
      const { oldVal, newVal } = hoursDiff;
      const parsedOld = oldVal || oldVal === 0 ? oldVal : '–';
      const parsedNew = newVal || newVal === 0 ? newVal : '–';
      condensed = `Changed student <span class="history-value">completed hours</span> in <span class="history-value">${activityNameCropped}</span> to <span class="history-value">${parsedNew}</span>`;
      expanded = `<span class="history-value">${user}</span> changed student <span class="history-value">completed hours</span> in <span class="history-value">${activityName}</span> from <span class="history-value">${parsedOld}</span> to <span class="history-value">${parsedNew}</span>`;
      // accounts for text shape when hours and status have changed
      if (parsedStatus) {
        expanded += ` and marked <span class="history-value">${parsedStatus}</span>`;
      } else if (statusDiff && statusDiff.oldVal && !statusDiff.newVal) {
        expanded += ` and cleared completion status`;
      }
    } else if (parsedStatus) {
      // accounts for only status change
      condensed = `Marked student <span class="history-value">${parsedStatus}</span> in <span class="history-value">${activityNameCropped}</span>`;
      expanded = `<span class="history-value">${user}</span> marked student <span class="history-value">${parsedStatus}</span> in <span class="history-value">${activityName}</span>`;
    } else if (statusDiff && !statusDiff.newVal) {
      // accounts for only status cleared
      condensed = `Cleared <span class="history-value">completion status</span> in <span class="history-value">${activityNameCropped}</span>`;
      expanded = `<span class="history-value">${user}</span> cleared completion status in <span class="history-value">${activityName}</span>`;
    }
    return { condensed, expanded };
  }

  createStudentActivityRemovedRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      metaData: { activityName },
    } = docLog;

    const activityNameCropped = this.truncate(activityName);
    const user = this.parseCreatedBy(createdBy);

    const condensed = `Removed student from <span class="history-value">${activityNameCropped}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> removed student ` +
      `from <span class="history-value">${activityName}</span>`;
    return { condensed, expanded };
  }

  // ignored until language is finalized (JE)
  /* istanbl ignore next */
  createStudentAssessmentScoreRecord(docLog: IDocLog): IHistoryRecordText {
    const {
      createdBy,
      diffs,
      metaData: { assessmentNickName, adminNumber },
    } = docLog;
    const user = this.parseCreatedBy(createdBy);
    const { newVal } = find(diffs, diff => diff.path === 'items');
    const parsedVal = reduce(
      newVal,
      (result, score, key) => {
        const section = score.section;
        const value = score.value ? score.value : '-';
        result += `<span class="history-value">${section}</span> to <span class="history-value">${value} and</span> `;
        return result;
      },
      '',
    );
    const trimmedValue = parsedVal.substring(0, parsedVal.length - 11);
    const condensed =
      `<span class="history-value">${assessmentNickName} Admin ${adminNumber}</span> ` + `${trimmedValue}</span>`;
    const expanded =
      `<span class="history-value">${user}</span> changed <span class="history-value">${assessmentNickName} Admin ${adminNumber}</span> ` +
      `${trimmedValue}</span>`;
    return { condensed, expanded };
  }
}
